// Prospect-facing Decision Room view — the customer-side counterpart
// to BuyerSpaceDetail (seller view).
//
// Auth pattern: token in URL query (?t=<token>). No login flow,
// no Bearer header, just one fetch with the token in the query.
//
// Layout mirrors the seller side — left sidebar with the 9 sections,
// main panel renders whichever section is active. Sections that the
// seller has hidden simply don't appear in the sidebar (filtered
// server-side via section_visibility). Editing is replaced with
// read-only displays everywhere except:
//   - MAP: prospect can check off items they own + post comments
//   - Conversations: prospect can post messages (TODO next session)
//
// Mounts at /space/:slug — see App.jsx routing wire-up.

import { useEffect, useRef, useState } from 'react';
import { whenHuman } from '../utils/humanPresence';
import ReelThumb from './ReelThumb';
import ShareViewer from './ShareViewer';
import { splitRecordings, isFullRecording, kindLabel, kindBlurb, countsSummary } from '../utils/roomRecordings';
import MentionInput, { filterMentionsAgainstBody, renderBodyWithMentions } from './MentionInput';
import InlineMarkdown from './InlineMarkdown';
import RoomFormsProspectPanel from './RoomFormsProspectPanel';
import ProposalProspectPanel from './ProposalProspectPanel';
import {
  fileKind, formatBytes, formatPageCount, roomDocBase, roomDocPageUrl,
} from '../utils/docDisplay';

// A preview is the seller's own credential; the server refuses writes on
// it (PREVIEW_READ_ONLY). Shown in place of every composer so the seller
// isn't invited to type something that can't reach anyone.
function PreviewReadOnlyNote({ what = 'Messages' }) {
  return (
    <div style={{
      padding: '10px 12px', borderRadius: 8, background: '#fef3c7',
      color: '#92400e', fontSize: 13, lineHeight: 1.45,
    }}>
      {what} can’t be sent from a preview. To reach this person, share the
      Decision Room with them — the preview is only for checking how it looks.
    </div>
  );
}

const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

const SECTION_LABELS = {
  welcome: 'Welcome',
  discovery: "Why We're Here",
  demos: 'Demos',
  documents: 'Documents',
  map: 'Mutual Action Plan',
  people: 'People',
  conversations: 'Conversations',
  roi: 'ROI',
  customer_stories: 'Customer Stories',
  forms: 'Forms',
  proposal: 'Proposal',
};

// Per-section metadata used by the sidebar (icon) and hero header
// (eyebrow tagline). Icons are inline 18x18 stroke SVGs so they
// inherit color from the parent and don't pull a new font dep.
// Taglines are short — they sit under the H1 as a contextual hint
// for the prospect, not a paragraph.
const SECTION_META = {
  welcome: {
    eyebrow: 'Start here',
    tagline: 'A short note from your seller about what you\'ll find inside.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 12l2-2 4 4 8-8 4 4" /><path d="M3 21h18" />
      </svg>
    ),
  },
  discovery: {
    eyebrow: 'Context',
    tagline: 'What we heard on our discovery call, in your own words.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
      </svg>
    ),
  },
  demos: {
    eyebrow: 'Watch',
    tagline: 'Personalized trailers of our walkthroughs — share with your team.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="5 3 19 12 5 21 5 3" />
      </svg>
    ),
  },
  documents: {
    eyebrow: 'Reference',
    tagline: 'Pricing, security, and everything else we discussed — all in one place.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
      </svg>
    ),
  },
  map: {
    eyebrow: 'On track',
    tagline: 'Our shared checklist from now to decision day.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
  people: {
    eyebrow: 'The room',
    tagline: 'Both sides of the table — who you\'ll be working with.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  conversations: {
    eyebrow: 'Talk to us',
    tagline: 'Ask questions, drop comments, request a follow-up — we reply right here.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  roi: {
    eyebrow: 'The math',
    tagline: 'What this looks like in dollars for your team.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" />
      </svg>
    ),
  },
  customer_stories: {
    eyebrow: 'Proof',
    tagline: 'Companies like yours, the wins they\'ve had since rolling us out.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polygon points="12 2 15 8.5 22 9.5 17 14.5 18.5 21.5 12 18 5.5 21.5 7 14.5 2 9.5 9 8.5 12 2" />
      </svg>
    ),
  },
  forms: {
    eyebrow: 'Your input',
    tagline: 'A few questions from your seller — answers go straight to them.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="M9 14l2 2 4-4" />
      </svg>
    ),
  },
  proposal: {
    eyebrow: 'Proposal',
    tagline: 'What we\'re proposing, what it costs, and how to say yes.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><path d="M9 15h6M9 11h2" />
      </svg>
    ),
  },
};

// Off-screen but readable by assistive tech. Same values DocPages uses
// for its page-text layer — kept literal here rather than shared
// because this file must not import from the lazy document chunk.
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)',
  whiteSpace: 'nowrap', border: 0,
};

// ─── Responsive rules ─────────────────────────────────────────────
//
// This file is inline-styles-only, and inline styles can't be beaten
// by a stylesheet without !important — hence the shouting below. The
// room shipped with a hard 256px sidebar and 32px/36px main padding
// and simply did not reflow under ~700px.
//
// The mobile sidebar becomes a HORIZONTAL SCROLLING STRIP rather than
// a <select>. Two reasons: the sections keep their icons and their
// identity as real <button>s (a select would flatten nine labelled
// destinations into one control and lose the icon language the desktop
// nav is built on), and the room already uses exactly this pattern for
// the multi-demo picker, so it's the one the prospect has seen. The
// strip owns its own overflow-x, which is what keeps the PAGE from
// scrolling sideways.
const ROOM_RESPONSIVE_CSS = `
.tc-room-nav { scrollbar-width: thin; }
.tc-room-nav::-webkit-scrollbar { height: 6px; }
.tc-room-nav::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
@media (max-width: 860px) {
  .tc-room-body { flex-direction: column !important; }
  .tc-room-nav {
    width: auto !important;
    flex-direction: row !important;
    align-items: center;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    border-right: none !important;
    border-bottom: 1px solid #e2e8f0;
    padding: 10px 12px !important;
    gap: 8px !important;
  }
  .tc-room-nav > button {
    flex: 0 0 auto;
    white-space: nowrap;
    padding: 9px 14px !important;
  }
  .tc-room-naveyebrow, .tc-room-navspacer, .tc-room-navfoot { display: none !important; }
  .tc-room-main { padding: 20px 16px !important; }
  .tc-room-header { padding: 12px 16px !important; gap: 12px !important; }
  .tc-room-headtitle { flex: 1 1 100% !important; order: 3; }
}
@media (max-width: 520px) {
  .tc-room-main { padding: 16px 12px !important; }
  .tc-room-header { padding: 12px !important; }
}
`;

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return s; }
}

function fmtMoney(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString()}`;
}

export default function BuyerSpaceProspectView({ slug }) {
  // Token comes from the query string — pulled once on mount, kept
  // stable so a user playing with the URL doesn't lose their session.
  const [token] = useState(() => {
    if (typeof window === 'undefined') return '';
    const url = new URL(window.location.href);
    return url.searchParams.get('t') || url.searchParams.get('token') || '';
  });

  // Deep link into one asset (rooms_message_asset_links). The email a
  // seller's message produces carries ?section=documents&doc=<id> (or
  // &trailer=<id>) on top of the tracked link, so the reader lands on
  // the thing that was pointed at rather than on Welcome. Read once on
  // mount, honoured once the room has loaded and only if that section
  // is visible to this person; anything unknown is ignored.
  const [deepLink] = useState(() => {
    if (typeof window === 'undefined') return null;
    const url = new URL(window.location.href);
    const section = url.searchParams.get('section');
    const docId = url.searchParams.get('doc');
    const trailerId = url.searchParams.get('trailer');
    if (!section && !docId && !trailerId) return null;
    return { section, docId, trailerId };
  });

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [forwardOpen, setForwardOpen] = useState(false);
  // Which asset to bring into view in the active section. `nonce`
  // changes on every navigation so clicking the same chip twice still
  // scrolls the card back into view.
  const [focus, setFocus] = useState(() => (deepLink
    ? { docId: deepLink.docId || null, trailerId: deepLink.trailerId || null, nonce: 1 }
    : { docId: null, trailerId: null, nonce: 0 }));

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      if (!token) throw new Error('Missing access token in URL');
      const r = await fetch(
        `${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}?t=${encodeURIComponent(token)}`
      );
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load (status ${r.status})`);
      }
      const json = await r.json();
      setData(json);
      // Default to the first visible section. Welcome usually wins —
      // unless the link asked for a section this person can see.
      const visible = json.buyerSpace.visibleSections || [];
      const first = visible[0];
      const requested = deepLink && deepLink.section && visible.includes(deepLink.section)
        ? deepLink.section
        : null;
      setActiveSection((prev) => prev || requested || first || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [slug, token]);

  // A chip in a message was clicked: switch to the section the asset
  // lives in and bring it into view. Section comes from the room's
  // CURRENT data (a document's category can change after the message
  // was written), never from the stored ref.
  const openAsset = (ref) => {
    if (!data || !ref) return;
    const visible = data.buyerSpace.visibleSections || [];
    if (ref.kind === 'trailer') {
      if (!visible.includes('demos')) return;
      setActiveSection('demos');
      setFocus((f) => ({ docId: null, trailerId: ref.id, nonce: f.nonce + 1 }));
      return;
    }
    const doc = (data.docs || []).find((d) => d.id === ref.id);
    if (!doc) return;
    const section = doc.category === 'customer_story' ? 'customer_stories' : 'documents';
    if (!visible.includes(section)) return;
    setActiveSection(section);
    setFocus((f) => ({ docId: ref.id, trailerId: null, nonce: f.nonce + 1 }));
  };

  // View-tracking beacon — fires once when the prospect lands on
  // /space/<slug>?t=<token>. Reuses the existing /api/trailers/view
  // endpoint (same token-based attribution model) so the engagement
  // panel automatically picks up Decision Room visits without any
  // schema changes. event='view' attributes the visit to this
  // prospect's distribution; the seller's engagement panel flips
  // "Not yet opened" → engaged on first hit.
  //
  // text/plain Blob avoids the CORS preflight (matches the
  // ShareViewer beacon fix from 2026-04-26 — see
  // reference_external_api_limits.md). One beacon per page mount,
  // guarded against React StrictMode double-invocation in dev.
  // Both beacons below wait for a real input event (2026-08-25).
  // Corporate mail filters fetch and DETONATE every link in a message
  // while deciding whether to deliver it — Defender Safe Links runs a
  // full headless Chrome that executes this app, so anything fired on
  // load registered a prospect "opening" mail that was sitting in
  // QUARANTINE, and the seller got a notification for a visit that
  // never happened. A detonation sandbox does not synthesize input;
  // a human cannot read this page without producing some.
  useEffect(() => {
    if (!token || typeof window === 'undefined') return;
    whenHuman(() => {
      const url = `${API_BASE}/api/trailers/view/${encodeURIComponent(token)}`;
      const body = JSON.stringify({ event: 'view' });
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
          return;
        }
      } catch (_) { /* fall through to fetch */ }
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body,
        keepalive: true,
      }).catch(() => { /* analytics must never break the page */ });
    });
  }, [token]);

  // The qualified space-visit beacon. The server used to record the
  // visit (and notify the owner) on the room GET itself; that
  // recording now lives behind this POST, which only ever fires after
  // human input. Guarded against React StrictMode double-invocation —
  // the server dedups notifications per 6h anyway, but there is no
  // reason to send twice.
  const visitFiredRef = useRef(false);
  useEffect(() => {
    if (!token || !slug || typeof window === 'undefined') return;
    whenHuman(() => {
      if (visitFiredRef.current) return;
      visitFiredRef.current = true;
      const url = `${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}/visit?t=${encodeURIComponent(token)}`;
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url);
          return;
        }
      } catch (_) { /* fall through to fetch */ }
      fetch(url, { method: 'POST', keepalive: true })
        .catch(() => { /* analytics must never break the page */ });
    });
  }, [token, slug]);

  // Brand color theming — apply the seller's primary color to accent
  // surfaces (sidebar active row, primary buttons). Falls back to
  // brand purple when no color is configured.
  const accent = data?.branding?.brandPrimaryColor || '#7c3aed';

  // Only blank the screen on the FIRST load. After data has landed,
  // background reloads (triggered by posting a message, ticking a MAP
  // item, etc.) keep the existing UI mounted instead of flashing the
  // loader.
  if (loading && !data) {
    return <CenteredMessage message="Loading your Decision Room…" />;
  }
  if (error) {
    return <CenteredMessage message={`Couldn't load this Decision Room: ${error}`} tone="error" />;
  }
  if (!data) return null;

  const space = data.buyerSpace;
  const visibleSections = space.visibleSections || [];

  return (
    // Prospect view is always light — this is a branded external
    // experience that should look identical regardless of the
    // (in-app) viewer's theme preference. data-theme="light" +
    // colorScheme prevents any dark-mode CSS from cascading in
    // when this component renders inside the seller's "Preview as
    // prospect" frame on a dark dashboard.
    <div data-theme="light" style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #f5f3ff 100%)',
      color: '#0f172a',
      colorScheme: 'light',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Media queries live in a <style> tag because this component is
          inline-styles-only (same approach AskWinter and
          RecordTrailerPage already take in this repo). */}
      <style>{ROOM_RESPONSIVE_CSS}</style>

      {/* ─── Branded header ─────────────────────────────────────── */}
      <Header
        branding={data.branding}
        viewer={data.viewer}
        prospectCompanyName={space.prospectCompanyName}
        accent={accent}
        // Forward gate (2026-05-04): only render the Forward button
        // when the viewer's access_level allows it. 'admin' and
        // legacy NULL → allowed (legacy distributions pre-date the
        // People-tab access controls; we don't lock those out).
        // 'member' → button hidden so they can't try to forward
        // (the backend would deny anyway, this just makes the UX
        // explicit about why the option isn't there).
        onForwardClick={data.viewer?.accessLevel === 'member'
          ? null
          : () => setForwardOpen(true)}
      />

      {/* ─── Forward modal ──────────────────────────────────────── */}
      {forwardOpen && (
        <ForwardDecisionRoomModal
          slug={slug}
          token={token}
          accent={accent}
          branding={data.branding}
          prospectCompanyName={space.prospectCompanyName}
          onClose={() => setForwardOpen(false)}
        />
      )}

      {/* ─── Forwarded-from banner (when a champion shared the link) */}
      {data.viewer?.forwardedFrom?.name && (
        <div style={{
          background: '#eff6ff', borderBottom: '1px solid #bfdbfe',
          padding: '10px 24px', textAlign: 'center', fontSize: 14, color: '#1e40af',
        }}>
          ↳ <strong>{data.viewer.forwardedFrom.name}</strong> shared this Decision Room with you.
        </div>
      )}

      <div className="tc-room-body" style={{ display: 'flex', flex: 1, minWidth: 0, minHeight: 0 }}>
        {/* ─── Left sidebar ─────────────────────────────────────── */}
        {/* Becomes a horizontal scrolling section strip under 860px —
            see ROOM_RESPONSIVE_CSS. */}
        <aside className="tc-room-nav" aria-label="Decision Room sections" style={{
          width: 256, flex: 'none', background: '#fff', borderRight: '1px solid #e2e8f0',
          padding: '24px 14px', display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {/* Sidebar eyebrow — gives the column an identity */}
          <div className="tc-room-naveyebrow" style={{
            margin: '0 6px 14px', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.12em', textTransform: 'uppercase',
            color: '#94a3b8',
          }}>
            Decision Room
          </div>
          {visibleSections.map((id) => {
            const isActive = activeSection === id;
            const meta = SECTION_META[id];
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveSection(id)}
                aria-current={isActive ? 'page' : undefined}
                style={{
                  position: 'relative',
                  textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 11,
                  padding: '11px 14px', paddingLeft: isActive ? 16 : 14,
                  borderRadius: 10,
                  border: 'none', cursor: 'pointer',
                  background: isActive
                    ? `linear-gradient(90deg, ${accent}1a 0%, ${accent}08 60%, transparent 100%)`
                    : 'transparent',
                  color: isActive ? accent : '#475569',
                  fontWeight: isActive ? 600 : 500, fontSize: 14,
                  fontFamily: 'inherit',
                  transition: 'background 0.15s ease, color 0.15s ease, padding-left 0.15s ease',
                }}
                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#f1f5f9'; }}
                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
              >
                {/* Accent bar on active */}
                {isActive && (
                  <span aria-hidden="true" style={{
                    position: 'absolute', left: 6, top: 8, bottom: 8,
                    width: 3, borderRadius: 2, background: accent,
                  }} />
                )}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 22, height: 22, flex: 'none',
                  color: isActive ? accent : '#94a3b8',
                  transition: 'color 0.15s ease',
                }}>
                  {meta?.icon || null}
                </span>
                <span>{SECTION_LABELS[id] || id}</span>
              </button>
            );
          })}

          {/* Footer attribution at the bottom of the sidebar — soft brand
              presence without being pushy. */}
          <div className="tc-room-navspacer" style={{ flex: 1 }} />
          <div className="tc-room-navfoot" style={{
            margin: '12px 6px 0', fontSize: 11, color: '#94a3b8',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{
              display: 'inline-flex', width: 6, height: 6, borderRadius: '50%',
              background: accent,
            }} />
            Powered by TrailerCast
          </div>
        </aside>

        {/* ─── Main panel ───────────────────────────────────────── */}
        {/* Widened from 920 → 1320 max-width so the embedded
            ShareViewer (whose standalone container is ~1320 too) and
            the multi-demo picker both have room to breathe. Padding
            scales down slightly on the horizontal axis since the
            wider canvas already provides visual margin. */}
        {/* minWidth:0 is load-bearing — without it this flex child
            refuses to shrink below its content and the whole page
            scrolls sideways on a phone. */}
        <main className="tc-room-main" style={{ flex: 1, minWidth: 0, padding: '32px 36px', overflow: 'auto' }}>
          <div style={{ maxWidth: 1320, margin: '0 auto', minWidth: 0 }}>
            <ProspectSection
              section={activeSection}
              data={data}
              accent={accent}
              slug={slug}
              token={token}
              onReload={load}
              focus={focus}
              onOpenAsset={openAsset}
            />
          </div>
        </main>
      </div>
    </div>
  );
}

// ─── Header (branded with seller's company logo + colors) ──────

function Header({ branding, viewer, prospectCompanyName, accent, onForwardClick }) {
  const seller = branding || {};
  // Match ShareViewer's "Prepared by" treatment: show profile picture
  // (or initials fallback), seller name + title byline, and optional
  // company logo to the left. Same visual language so a prospect
  // who's seen a /share/<token> page recognizes the source.
  const initials = (seller.creatorName || 'TC')
    .split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  const byline = [seller.creatorName, seller.creatorJobTitle].filter(Boolean).join(', ');

  return (
    <header className="tc-room-header" style={{
      background: '#fff', borderBottom: '1px solid #e2e8f0',
      padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 16,
      flexWrap: 'wrap',
    }}>
      {seller.companyLogoUrl && (
        <img
          src={seller.companyLogoUrl}
          alt={seller.creatorCompany || ''}
          style={{ height: 36, maxWidth: 120, objectFit: 'contain', flex: 'none' }}
        />
      )}

      {/* Profile picture or initials fallback — mirrors
          .share-viewer__seller-avatar from ShareViewer. */}
      {seller.creatorProfilePictureUrl ? (
        <img
          src={seller.creatorProfilePictureUrl}
          alt={seller.creatorName || 'Sender avatar'}
          style={{
            width: 44, height: 44, borderRadius: '50%',
            objectFit: 'cover', flex: 'none',
            border: `2px solid ${accent}`,
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          style={{
            width: 44, height: 44, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `${accent}1a`, color: accent,
            fontSize: 15, fontWeight: 700, flex: 'none',
            border: `2px solid ${accent}`,
          }}
        >
          {initials}
        </div>
      )}

      <div className="tc-room-headtitle" style={{ flex: '1 1 180px', minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {seller.creatorCompany || 'TrailerCast'} <span style={{ color: '#cbd5e1', fontWeight: 400, margin: '0 6px' }}>×</span> {prospectCompanyName}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          Prepared by <strong style={{ color: '#334155', fontWeight: 600 }}>{byline || 'your TrailerCast user'}</strong>
        </div>
      </div>

      {viewer?.isPreview ? (
        <div style={{
          padding: '6px 12px', borderRadius: 100, background: '#fef3c7',
          color: '#92400e', fontSize: 13, fontWeight: 600,
        }}>
          Preview — nothing here is recorded or sent
        </div>
      ) : viewer?.stakeholderName && (
        <div style={{
          padding: '6px 12px', borderRadius: 100, background: `${accent}12`,
          color: accent, fontSize: 13, fontWeight: 600,
        }}>
          Viewing as {viewer.stakeholderName}
        </div>
      )}

      {/* Top-level "Forward this Decision Room" button. Visible from
          every section, not buried inside the trailer's share viewer.
          Earlier the only forwarding flow lived in the embedded
          ShareViewer on the Demos tab — prospects on MAP, Documents,
          or any other tab had no obvious way to forward. */}
      {onForwardClick && (
        <button
          type="button"
          onClick={onForwardClick}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 100,
            background: accent, color: '#fff', border: 'none',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            boxShadow: `0 4px 12px ${accent}33`,
            fontFamily: 'inherit',
            transition: 'transform 0.12s ease, box-shadow 0.12s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = `0 6px 16px ${accent}55`;
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = `0 4px 12px ${accent}33`;
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          <span>Forward to your team</span>
        </button>
      )}
    </header>
  );
}

// ─── Section router ────────────────────────────────────────────

function ProspectSection({ section, data, accent, slug, token, onReload, focus, onOpenAsset }) {
  if (!section) return <CenteredMessage message="Pick a section from the left." />;

  const space = data.buyerSpace;

  if (section === 'welcome') {
    return (
      <div>
        <SectionHero section="welcome" accent={accent} />
        <Card>
          {space.welcomeVideoUrl && (
            <div style={{ marginBottom: 20, position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 12, overflow: 'hidden' }}>
              {/* Iframe-friendly providers (Loom, Vidyard) work directly. Direct .mp4 falls back to a video tag. */}
              {/\.(mp4|webm|mov)(\?|$)/i.test(space.welcomeVideoUrl)
                ? <video src={space.welcomeVideoUrl} controls style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
                : <iframe
                    src={space.welcomeVideoUrl}
                    title="Welcome video"
                    frameBorder="0"
                    allow="autoplay; fullscreen"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                  />}
            </div>
          )}
          {/* AI-generated welcome — render through InlineMarkdown so
              **bold**, *italic*, `code`, and links work. Placeholder
              text (no welcome yet) is plain so no markdown leaks. */}
          {space.welcomeMessage ? (
            <InlineMarkdown
              text={space.welcomeMessage}
              style={{ fontSize: 16, lineHeight: 1.6, color: '#334155' }}
            />
          ) : (
            <div style={{ fontSize: 16, lineHeight: 1.6, color: '#334155' }}>
              No welcome message yet.
            </div>
          )}
        </Card>
      </div>
    );
  }

  if (section === 'discovery') {
    return (
      <div>
        <SectionHero section="discovery" accent={accent} />
        <Card>
          {space.discoverySummary ? (
            <InlineMarkdown
              text={space.discoverySummary}
              style={{ fontSize: 15, lineHeight: 1.7, color: '#334155' }}
            />
          ) : (
            <div style={{ fontSize: 15, lineHeight: 1.7, color: '#334155' }}>
              Discovery summary not added yet.
            </div>
          )}
        </Card>
      </div>
    );
  }

  if (section === 'demos') {
    return <DemosPanel trailers={data.trailers} accent={accent} focusTrailerId={focus?.trailerId || null} focusNonce={focus?.nonce || 0} />;
  }

  if (section === 'documents' || section === 'customer_stories') {
    return (
      <DocsPanel
        section={section}
        allDocs={data.docs}
        viewer={data.viewer}
        accent={accent}
        slug={slug}
        token={token}
        onReload={onReload}
        focusDocId={focus?.docId || null}
        focusNonce={focus?.nonce || 0}
      />
    );
  }

  if (section === 'map') {
    return <MapProspectPanel data={data} accent={accent} slug={slug} token={token} onReload={onReload} />;
  }

  if (section === 'people') {
    const sellerSide = data.people.filter((p) => p.side === 'seller');
    const buyerSide = data.people.filter((p) => p.side === 'buyer');
    return (
      <div>
        <SectionHero section="people" accent={accent} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 16 }}>
          <PeopleCard title={`${data.branding.creatorCompany || 'Seller'} team`} people={sellerSide} />
          <PeopleCard title="Your team" people={buyerSide} />
        </div>
      </div>
    );
  }

  if (section === 'conversations') {
    return (
      <ProspectConversationsPanel
        conversation={data.conversation}
        docs={data.docs}
        trailers={data.trailers}
        viewer={data.viewer}
        token={token}
        accent={accent}
        onReload={onReload}
        people={data.people || []}
        onOpenAsset={onOpenAsset}
      />
    );
  }

  if (section === 'forms') {
    return (
      <div>
        <SectionHero section="forms" accent={accent} />
        <RoomFormsProspectPanel slug={slug} token={token} accent={accent} sellerCompanyName={data.branding && data.branding.creatorCompany} />
      </div>
    );
  }

  if (section === 'proposal') {
    return (
      <div>
        <SectionHero section="proposal" accent={accent} />
        <ProposalProspectPanel slug={slug} token={token} accent={accent} viewer={data.viewer} />
      </div>
    );
  }

  if (section === 'roi') {
    const roi = data.roi || {};
    const savings = roi.projectedSavingsCents != null ? roi.projectedSavingsCents / 100 : null;
    const cost = roi.currentCostCents != null ? roi.currentCostCents / 100 : null;
    const pctSaved = (savings != null && cost != null && cost > 0)
      ? Math.round((savings / cost) * 100) : null;
    return (
      <div>
        <SectionHero section="roi" accent={accent} />
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
            <RoiTile label="Current annual cost" value={fmtMoney(roi.currentCostCents)} accent={accent} />
            <RoiTile label="Projected annual savings" value={fmtMoney(roi.projectedSavingsCents)} accent={accent} highlight />
            <RoiTile label="Payback period" value={roi.paybackMonths != null ? `${roi.paybackMonths} mo` : '—'} accent={accent} />
            {pctSaved != null && <RoiTile label="Cost reduction" value={`${pctSaved}%`} accent={accent} />}
          </div>
          {roi.justification && (
            <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid #e2e8f0' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b' }}>
                How we got here
              </h3>
              <InlineMarkdown
                text={roi.justification}
                style={{ fontSize: 14, lineHeight: 1.6, color: '#334155' }}
              />
            </div>
          )}
        </Card>
      </div>
    );
  }

  return <Card><p style={{ margin: 0, color: '#94a3b8' }}>Section not recognized.</p></Card>;
}

// ─── Conversations panel — read + post inline ──────────────────
// Posts go through the existing /api/trailers/message/<token> endpoint
// (same wire the share-page chat uses). The token in our URL is the
// distribution token, so the backend's existing handler accepts it
// without any new route plumbing. After a successful post we
// onReload() the parent to refetch the public Decision Room payload —
// the new message appears at the bottom of the thread.

function ProspectConversationsPanel({ conversation, docs, trailers, viewer, token, accent, onReload, people, onOpenAsset }) {
  // Comments written from a document card land in this same thread —
  // they ARE room messages, tagged with what they're about. Without the
  // tag they read as context-free ("is this the latest version?" —
  // which version of what?), so every message carrying a
  // referencedDocId gets an "on: <title>" chip naming its document.
  const docTitleById = {};
  (docs || []).forEach((d) => { docTitleById[d.id] = d.displayTitle || d.filename; });

  // Asset links inside a message (rooms_message_asset_links) open the
  // asset — but only while it is still in the room and visible to this
  // person. The payload above is already filtered that way, so
  // "present in the payload" is the availability test; a chip for a
  // document hidden since the message was written renders as a plain,
  // struck label instead of a dead link.
  const availableAssets = new Set([
    ...(docs || []).map((d) => `doc:${d.id}`),
    ...(trailers || []).map((t) => `trailer:${t.id}`),
  ]);
  const isAssetAvailable = (ref) => availableAssets.has(`${ref.kind}:${ref.id}`);

  const [draft, setDraft] = useState('');
  const [draftMentions, setDraftMentions] = useState([]);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);

  const send = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    setError(null);
    try {
      // Strip mentions whose @-text the prospect backspaced out
      // before posting so notifications don't fire for ghosts.
      const finalMentions = filterMentionsAgainstBody(draft, draftMentions);
      const r = await fetch(`${API_BASE}/api/trailers/message/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'comment',
          // Self-identify from the token's stakeholder so the seller
          // sees who's posting. Falls back to email-only or anon when
          // the distribution doesn't carry a name.
          senderName: viewer?.stakeholderName || null,
          senderEmail: viewer?.stakeholderEmail || null,
          body: draft.trim(),
          mentions: finalMentions,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Send failed (${r.status})`);
      }
      setDraft('');
      setDraftMentions([]);
      onReload();
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#0f172a' }}>
          {SECTION_LABELS.conversations}
        </h1>
      </div>
      <Card>
        {(!conversation || conversation.length === 0) ? (
          <p style={{ margin: '0 0 16px', color: '#94a3b8' }}>
            No messages yet. Anything you write here goes straight to the seller.
          </p>
        ) : (
          <ul style={{ margin: '0 0 16px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {conversation.map((m) => {
              const isFromSeller = m.senderKind === 'seller';
              return (
                <li key={m.id} style={{
                  background: isFromSeller ? '#f8fafc' : `${accent}10`,
                  padding: '10px 14px', borderRadius: 10,
                  alignSelf: isFromSeller ? 'flex-start' : 'flex-end',
                  maxWidth: '85%',
                }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    <strong style={{ color: '#0f172a' }}>
                      {m.senderName || (isFromSeller ? 'Seller' : 'You')}
                    </strong>
                    {' · '}
                    {new Date(m.createdAt).toLocaleString()}
                  </div>
                  {m.referencedDocId && (
                    <div style={{ marginBottom: 5 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        fontSize: 11, fontWeight: 600, padding: '2px 9px',
                        borderRadius: 999, background: '#fff',
                        border: '1px solid #e2e8f0', color: '#475569',
                        maxWidth: '100%', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        on: {docTitleById[m.referencedDocId] || 'a document'}
                      </span>
                    </div>
                  )}
                  <div style={{ fontSize: 14, color: '#334155', whiteSpace: 'pre-wrap' }}>
                    {renderBodyWithMentions(m.body, m.mentions || [], {
                      highlightEmail: viewer?.stakeholderEmail,
                      assetRefs: m.assetRefs || [],
                      onAssetClick: onOpenAsset,
                      isAssetAvailable,
                    })}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Reply form — always visible at the bottom so the prospect
            never has to hunt for "where do I respond". Honeypot
            field omitted because the existing /api/trailers/message
            endpoint handles it for the share page; if spam becomes a
            problem we can add the same hidden field here. */}
        {viewer?.isPreview ? (
          <div style={{
            paddingTop: conversation?.length ? 16 : 0,
            borderTop: conversation?.length ? '1px solid #e2e8f0' : 'none',
          }}>
            <PreviewReadOnlyNote />
          </div>
        ) : (
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8,
          paddingTop: conversation?.length ? 16 : 0,
          borderTop: conversation?.length ? '1px solid #e2e8f0' : 'none',
        }}>
          <MentionInput
            value={draft}
            onChange={setDraft}
            mentions={draftMentions}
            onMentionsChange={setDraftMentions}
            people={people || []}
            placeholder="Send a message to the seller… type @ to mention someone"
            rows={3}
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter sends — common chat convention. Plain
              // Enter inserts a newline so multi-line messages work.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send();
              }
            }}
          />
          {error && <span style={{ color: '#dc2626', fontSize: 12 }}>{error}</span>}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>⌘+Enter to send</span>
            <button
              type="button"
              onClick={send}
              disabled={!draft.trim() || posting}
              style={{
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: accent, color: '#fff', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                opacity: (!draft.trim() || posting) ? 0.5 : 1,
              }}
            >
              {posting ? 'Sending…' : 'Send message'}
            </button>
          </div>
        </div>
        )}
      </Card>
    </div>
  );
}

// ─── Demos panel — embedded ShareViewer + multi-demo picker ──────
// One trailer  → just embed ShareViewer using that trailer's viewerToken.
// 2+ trailers  → tab strip across the top so the prospect can switch
//             between demos without leaving the Decision Room.
// 0 trailers   → empty state.
//
// The viewerToken comes from the public Decision Room response and is
// either the prospect's existing per-recipient distribution OR a
// fresh one minted on first load (so engagement attributes correctly
// even for demos the prospect wasn't originally invited to).

function DemosPanel({ trailers, accent, focusTrailerId = null, focusNonce = 0 }) {
  const [activeReelId, setActiveReelId] = useState(
    (focusTrailerId && trailers.some((t) => t.id === focusTrailerId)) ? focusTrailerId : (trailers[0]?.id || null)
  );
  // A message chip (or the email's deep link) named a trailer: select
  // it. Keyed on the nonce so re-clicking the same chip re-selects.
  useEffect(() => {
    if (focusTrailerId && trailers.some((t) => t.id === focusTrailerId)) setActiveReelId(focusTrailerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTrailerId, focusNonce]);

  if (!trailers || trailers.length === 0) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#0f172a' }}>{SECTION_LABELS.demos}</h1>
        </div>
        <Card><p style={{ margin: 0, color: '#94a3b8' }}>No recordings in this Decision Room yet.</p></Card>
      </div>
    );
  }

  const activeReel = trailers.find((r) => r.id === activeReelId) || trailers[0];
  const groups = splitRecordings(trailers);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#0f172a' }}>{SECTION_LABELS.demos}</h1>
        {trailers.length > 1 && (
          <span style={{ fontSize: 13, color: '#94a3b8' }}>
            · {countsSummary(trailers)}
          </span>
        )}
      </div>

      {trailers.length > 1 && [
        ['Trailers', groups.trailers],
        ['Full recordings', groups.fullRecordings],
      ].filter(([, list]) => list.length > 0).map(([label, list]) => (
        <div key={label}>
          {groups.trailers.length > 0 && groups.fullRecordings.length > 0 && (
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', margin: '0 0 8px' }}>
              {label}
            </div>
          )}
          <DemoPicker trailers={list} activeReelId={activeReel.id} onSelect={setActiveReelId} accent={accent} />
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '0 0 12px', flexWrap: 'wrap' }}>
        <span style={{
          padding: '4px 10px', borderRadius: 999,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
          color: isFullRecording(activeReel) ? '#334155' : accent,
          background: isFullRecording(activeReel) ? '#f1f5f9' : `${accent}14`,
          border: `1px solid ${isFullRecording(activeReel) ? '#e2e8f0' : `${accent}33`}`,
        }}>
          {kindLabel(activeReel)}
        </span>
        <span style={{ fontSize: 13, color: '#64748b' }}>{kindBlurb(activeReel)}</span>
      </div>

      {activeReel.viewerToken ? (
        <div style={{
          background: '#fff', borderRadius: 16, padding: 16,
          border: '1px solid #e2e8f0',
        }}>
          <ShareViewer key={activeReel.viewerToken} token={activeReel.viewerToken} embedded />
        </div>
      ) : (
        <Card>
          <p style={{ margin: 0, color: '#dc2626' }}>
            This trailer can't be played yet — the share link wasn't created. Ask the seller to send a follow-up first.
          </p>
        </Card>
      )}
    </div>
  );
}

function DemoPicker({ trailers, activeReelId, onSelect, accent }) {
  // Use a tab strip when there are 2-4 trailers; switch to a dropdown
  // when there are 5+ to keep the UI from getting busy.
  if (trailers.length >= 5) {
    return (
      <div style={{ marginBottom: 16 }}>
        <select
          value={activeReelId}
          onChange={(e) => onSelect(e.target.value)}
          style={{
            padding: '10px 14px', borderRadius: 10, border: '1px solid #cbd5e1',
            fontSize: 14, fontFamily: 'inherit', background: '#fff', minWidth: 280,
          }}
        >
          {trailers.map((r) => (
            <option key={r.id} value={r.id}>
              {r.prospectName || r.filename} — {fmtDate(r.completedAt || r.createdAt)}
            </option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div style={{
      display: 'flex', gap: 8, marginBottom: 16,
      borderBottom: '1px solid #e2e8f0', paddingBottom: 12,
      flexWrap: 'wrap',
    }}>
      {trailers.map((r) => {
        const isActive = r.id === activeReelId;
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => onSelect(r.id)}
            style={{
              padding: '8px 14px', borderRadius: 8,
              border: isActive ? `1px solid ${accent}` : '1px solid #e2e8f0',
              background: isActive ? `${accent}10` : '#fff',
              color: isActive ? accent : '#475569',
              cursor: 'pointer', fontSize: 13, fontWeight: 600,
              fontFamily: 'inherit',
              transition: 'background 0.12s ease, border-color 0.12s ease',
            }}
          >
            {r.prospectName || r.filename}
            <span style={{
              marginLeft: 8, padding: '2px 7px', borderRadius: 999,
              fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
              color: isFullRecording(r) ? '#475569' : accent,
              background: isFullRecording(r) ? '#f1f5f9' : `${accent}14`,
            }}>
              {kindLabel(r)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Documents / Customer Stories — card grid (2026-08-23) ────────
//
// Was a vertical <ul> of filename rows whose only affordance was
// "Download", which made every document a decision to leave the room.
// Now it's a scannable grid where the primary action is VIEW: the
// document opens on its own page (/space/<slug>/doc/<id>?t=…) that
// renders the server's pre-rendered page images and reports reading
// back to the seller. Download stays, quietly, as the secondary.
//
// The card shows a page-1 thumbnail when the backend has one and a
// branded, file-type-tinted placeholder when it doesn't — never a
// broken image, including when /thumb 404s at request time (an evicted
// blob, a render that lost its thumbnail): the <img> onError swaps to
// the same placeholder the no-thumbnail case uses.

function DocsPanel({ section, allDocs, viewer, accent, slug, token, onReload, focusDocId = null, focusNonce = 0 }) {
  const docs = section === 'customer_stories'
    ? (allDocs || []).filter((d) => d.category === 'customer_story')
    : (allDocs || []).filter((d) => d.category !== 'customer_story');

  // Token-auth download — fetch the byte stream via the public
  // endpoint, then trigger a Blob-URL download so the browser saves
  // with the original filename. Unchanged from the list version; only
  // its visual weight on the card changed.
  const downloadDoc = async (doc) => {
    try {
      const r = await fetch(
        `${roomDocBase(API_BASE, slug, doc.id)}/download?t=${encodeURIComponent(token)}`
      );
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[prospect] download failed:', err.message);
      alert(`Couldn't download: ${err.message}`);
    }
  };

  return (
    <div>
      <SectionHero section={section} accent={accent} />
      {/* Buyer-side upload affordance, Documents section only.
          Customer Stories stays seller-curated (it's a marketing
          surface) so the upload button intentionally isn't shown
          there. */}
      {section === 'documents' && (
        <ProspectDocUpload
          slug={slug}
          token={token}
          accent={accent}
          onUploaded={onReload}
        />
      )}
      {docs.length === 0 ? (
        <Card><p style={{ margin: 0, color: '#94a3b8' }}>Nothing here yet.</p></Card>
      ) : (
        <ul
          className="tc-doc-grid"
          aria-label={`${SECTION_LABELS[section] || section} — ${docs.length} document${docs.length === 1 ? '' : 's'}`}
          style={{
            display: 'grid',
            // min() so a container narrower than 240px (a phone in
            // portrait with padding) still yields ONE column instead of
            // a 240px track that overflows the page.
            gridTemplateColumns: 'repeat(auto-fill, minmax(min(240px, 100%), 1fr))',
            gap: 16, margin: 0, padding: 0, listStyle: 'none',
          }}
        >
          {docs.map((d) => (
            <DocCard
              key={d.id}
              doc={d}
              accent={accent}
              slug={slug}
              token={token}
              viewer={viewer}
              onDownload={downloadDoc}
              onReload={onReload}
              focused={d.id === focusDocId}
              focusNonce={focusNonce}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function DocCard({ doc, accent, slug, token, viewer, onDownload, onReload, focused = false, focusNonce = 0 }) {
  const [thumbBroken, setThumbBroken] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  // Brought into view by a message chip or the email's deep link
  // (rooms_message_asset_links): scroll to the card and ring it for a
  // moment so the eye lands on the right one in a grid of look-alikes.
  const cardRef = useRef(null);
  const [ringing, setRinging] = useState(false);
  useEffect(() => {
    if (!focused) return undefined;
    try { cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) { /* noop */ }
    setRinging(true);
    const t = setTimeout(() => setRinging(false), 2600);
    return () => clearTimeout(t);
  }, [focused, focusNonce]);

  const kind = fileKind(doc.mimeType, doc.filename);
  const title = doc.displayTitle || doc.filename;
  const base = roomDocBase(API_BASE, slug, doc.id);
  const showThumb = Boolean(doc.hasThumbnail) && !thumbBroken;
  const commentCount = (doc.comments || []).length;

  const metaBits = [
    kind.label,
    formatPageCount(doc.pageCount),
    formatBytes(doc.sizeBytes),
  ].filter(Boolean);

  return (
    <li ref={cardRef} data-doc-id={doc.id} style={{
      display: 'flex', flexDirection: 'column',
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
      overflow: 'hidden', minWidth: 0,
      boxShadow: ringing
        ? `0 0 0 3px ${accent}, 0 8px 24px -12px rgba(15, 23, 42, 0.18)`
        : '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.08)',
      transition: 'box-shadow 0.4s ease',
    }}>
      {/* Thumbnail well. Decorative by construction: the accessible
          name for this document is the <h3> immediately below, exactly
          as ReelThumb's callers do it, so a screen reader announces the
          title once instead of "image, image, <title>". */}
      <div aria-hidden="true" style={{
        height: 148, flex: 'none', position: 'relative',
        borderBottom: '1px solid #f1f5f9',
        background: showThumb
          ? '#f1f5f9'
          : `linear-gradient(135deg, ${kind.accent}1f 0%, ${kind.accent}3d 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {showThumb ? (
          <img
            src={`${base}/thumb?t=${encodeURIComponent(token)}`}
            alt=""
            loading="lazy"
            decoding="async"
            // A 404 here (evicted blob, half-finished render) must not
            // leave a broken-image glyph on a buyer's screen — swap to
            // the branded placeholder instead.
            onError={() => setThumbBroken(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={kind.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.75 }}>
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.09em',
              textTransform: 'uppercase', color: kind.accent, opacity: 0.85,
            }}>
              {kind.label}
            </span>
          </div>
        )}
      </div>

      <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
          <h3 style={{
            margin: 0, fontSize: 14.5, fontWeight: 600, color: '#0f172a',
            lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere',
          }}>
            {title}
          </h3>
          {doc.uploadedByProspect && (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
              background: '#e0f2fe', color: '#075985', letterSpacing: 0.2, flex: 'none',
            }}>
              Uploaded by you
            </span>
          )}
        </div>

        {doc.description && (
          <div style={{
            fontSize: 12.5, color: '#64748b', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {doc.description}
          </div>
        )}

        <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
          {metaBits.join(' · ')}
        </div>
        <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{fmtDate(doc.uploadedAt)}</div>

        {/* Actions pinned to the bottom so a grid of cards with
            different description lengths still has one action line. */}
        <div style={{ marginTop: 'auto', paddingTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* A real anchor, not window.open: the destination URL is
              known synchronously at click time (unlike SalesLibraryPhase,
              which has to mint a token first), and a genuine
              target=_blank link is never popup-blocked, is
              middle-clickable, and is keyboard-operable for free. */}
          {doc.canView && (
            <a
              href={roomDocPageUrl(slug, doc.id, token)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', borderRadius: 8,
                background: accent, color: '#fff', textDecoration: 'none',
                fontSize: 13, fontWeight: 600,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" /><circle cx="12" cy="12" r="3" />
              </svg>
              View
            </a>
          )}
          <button
            type="button"
            onClick={() => onDownload(doc)}
            style={{
              padding: '8px 12px', borderRadius: 8,
              background: 'transparent', border: '1px solid transparent',
              color: '#475569', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            Download
          </button>
          <button
            type="button"
            onClick={() => setCommentsOpen((v) => !v)}
            aria-expanded={commentsOpen}
            title={`Comments on ${title}`}
            style={{
              marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '7px 10px', borderRadius: 8,
              background: commentsOpen ? `${accent}12` : 'transparent',
              border: '1px solid #e2e8f0',
              color: commentsOpen ? accent : '#64748b',
              cursor: 'pointer', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            <span>{commentCount}</span>
            <span style={SR_ONLY}>{`comment${commentCount === 1 ? '' : 's'} on ${title}`}</span>
          </button>
        </div>
      </div>

      {commentsOpen && (
        <DocCommentsThread
          doc={doc}
          slug={slug}
          token={token}
          accent={accent}
          viewer={viewer}
          onPosted={onReload}
        />
      )}
    </li>
  );
}

// ─── One document's comment thread ────────────────────────────────
//
// The recipient's OWN thread on this document — the server scopes it
// to their distribution, so a forwarded stakeholder writes privately
// and never sees the champion's notes. No account, no login: the room
// token in the URL is the whole credential, matching every other write
// on this surface (MAP check-off, room messages, uploads).
//
// The seed comes from the room payload (`doc.comments`), so opening the
// thread costs zero requests. After posting we append the server's own
// row optimistically and then refetch the thread, plus onReload() so
// the Conversations section — which lists the same message, chip-
// labelled with this document — stays in step.

// Reconcile a server thread against what's already on screen WITHOUT
// losing a row the reader just wrote. The server is authoritative for
// everything it returns; anything we hold that it doesn't mention yet
// (a POST that committed a beat before the read) is kept and re-sorted
// in rather than blinking out from under the person who typed it.
function mergeThread(serverRows, localRows) {
  const rows = Array.isArray(serverRows) ? serverRows : [];
  const seen = new Set(rows.map((c) => c.id));
  const orphans = (localRows || []).filter((c) => c && !seen.has(c.id));
  if (orphans.length === 0) return rows;
  return [...rows, ...orphans].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );
}

function DocCommentsThread({ doc, slug, token, accent, viewer, onPosted }) {
  const [thread, setThread] = useState(doc.comments || []);
  const [draft, setDraft] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);

  // The room payload is the source of truth; a background reload
  // (posting elsewhere, a MAP check-off) refreshes this thread too.
  useEffect(() => {
    setThread((prev) => mergeThread(doc.comments || [], prev));
  }, [doc.comments]);

  const url = `${roomDocBase(API_BASE, slug, doc.id)}/comments?t=${encodeURIComponent(token)}`;

  const refetch = async () => {
    try {
      const r = await fetch(url);
      if (!r.ok) return;
      const body = await r.json().catch(() => ({}));
      if (Array.isArray(body.comments)) {
        setThread((prev) => mergeThread(body.comments, prev));
      }
    } catch (_) {
      // The optimistic row is already on screen — a failed reconcile
      // is not worth an error message.
    }
  };

  const post = async () => {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    setError(null);
    try {
      const r = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: text,
          senderName: viewer?.stakeholderName || null,
          senderEmail: viewer?.stakeholderEmail || null,
          // Honeypot — real users never see this field, so any value
          // means a bot. Sent always, empty in the normal case.
          website_url: honeypot,
        }),
      });
      if (r.status === 429) {
        throw new Error('That was a lot of comments at once — give it a minute and try again.');
      }
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Couldn't post that (${r.status})`);
      }
      const body = await r.json().catch(() => ({}));
      setDraft('');
      setHoneypot('');
      if (body.comment) setThread((prev) => [...prev, body.comment]);
      refetch();
      if (typeof onPosted === 'function') onPosted();
    } catch (err) {
      setError(err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div style={{ borderTop: '1px solid #f1f5f9', background: '#f8fafc', padding: '12px 14px' }}>
      {thread.length === 0 ? (
        <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#94a3b8' }}>
          No comments yet. Ask a question about this document and the seller replies right here.
        </p>
      ) : (
        <ul style={{ margin: '0 0 10px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {thread.map((c) => (
            <li key={c.id} style={{
              background: c.senderKind === 'seller' ? '#fff' : `${accent}10`,
              border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px',
            }}>
              <div style={{ fontSize: 11.5, color: '#64748b', marginBottom: 3 }}>
                <strong style={{ color: '#0f172a' }}>
                  {c.senderName || (c.senderKind === 'seller' ? 'Seller' : 'You')}
                </strong>
                {' · '}
                {new Date(c.createdAt).toLocaleString()}
              </div>
              <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {c.body}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Honeypot. Off-screen rather than display:none (some bots skip
          hidden inputs), never focusable, never announced. */}
      <input
        type="text"
        name="website_url"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={SR_ONLY}
      />

      {viewer?.isPreview && <PreviewReadOnlyNote what="Comments" />}
      <label style={{ display: 'block' }}>
        <span style={SR_ONLY}>{`Comment on ${doc.displayTitle || doc.filename}`}</span>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder={viewer?.isPreview ? 'Comments can’t be sent from a preview' : 'Add a comment about this document…'}
          disabled={viewer?.isPreview || posting}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); }
          }}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1',
            fontSize: 13, fontFamily: 'inherit', resize: 'vertical', background: '#fff',
          }}
        />
      </label>
      {error && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
        <button
          type="button"
          onClick={post}
          disabled={viewer?.isPreview || !draft.trim() || posting}
          style={{
            padding: '7px 14px', borderRadius: 8, border: 'none',
            background: accent, color: '#fff',
            cursor: (!draft.trim() || posting) ? 'default' : 'pointer',
            fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
            opacity: (!draft.trim() || posting) ? 0.5 : 1,
          }}
        >
          {posting ? 'Posting…' : 'Post comment'}
        </button>
      </div>
    </div>
  );
}

// ─── Prospect document upload (2026-05-04) ───────────────────────
// Lets the prospect drop a doc into the Decision Room from the
// public view (signed NDA, security questionnaire, contract redline).
// Token-gated upload; the seller is notified per their preferences.
// 25 MB cap matches the seller-side multer config; mime errors come
// back as readable text so the prospect knows what went wrong.

const PROSPECT_UPLOAD_ACCEPT = [
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.png', '.jpg', '.jpeg', '.webp', '.gif',
  '.txt', '.csv', '.zip',
].join(',');

function ProspectDocUpload({ slug, token, accent, onUploaded }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const pick = () => {
    if (busy) return;
    setError(null);
    setSuccess(null);
    fileRef.current?.click();
  };

  const onPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file twice still fires
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setError('File is larger than 25 MB. Please compress or split it before uploading.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(
        `${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}/docs?t=${encodeURIComponent(token)}`,
        { method: 'POST', body: fd }
      );
      if (r.status === 429) {
        throw new Error('You\'ve uploaded a lot of files recently. Please wait an hour and try again.');
      }
      if (r.status === 503) {
        throw new Error('Uploads are temporarily unavailable. Please try again shortly or let the seller know.');
      }
      if (!r.ok) {
        const payload = await r.json().catch(() => ({}));
        throw new Error(payload.error || `Upload failed (${r.status})`);
      }
      const payload = await r.json();
      setSuccess(`Uploaded "${payload.doc?.displayTitle || file.name}".`);
      if (typeof onUploaded === 'function') onUploaded();
    } catch (err) {
      console.error('[prospect] doc upload failed:', err.message);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      marginBottom: 16,
      padding: '14px 16px',
      borderRadius: 12,
      border: '1px dashed #cbd5e1',
      background: '#f8fafc',
      display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>
          Upload a document
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
          Drop in a signed NDA, security questionnaire, or anything else the seller asked for. Max 25 MB.
        </div>
        {error && (
          <div style={{ fontSize: 12, color: '#dc2626', marginTop: 6 }}>{error}</div>
        )}
        {success && !error && (
          <div style={{ fontSize: 12, color: '#15803d', marginTop: 6 }}>{success}</div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={PROSPECT_UPLOAD_ACCEPT}
        onChange={onPicked}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        style={{
          padding: '9px 16px', borderRadius: 8,
          background: busy ? '#cbd5e1' : accent, color: '#fff', border: 'none',
          cursor: busy ? 'default' : 'pointer',
          fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
        }}
      >
        {busy ? 'Uploading…' : 'Choose file'}
      </button>
    </div>
  );
}

// ─── MAP panel — interactive checkoff + comments ─────────────────

function MapProspectPanel({ data, accent, slug, token, onReload }) {
  const [busyId, setBusyId] = useState(null);

  const toggle = async (action, checked) => {
    setBusyId(action.id);
    try {
      await fetch(`${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}/actions/${action.id}/checkoff?t=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checked }),
      });
      onReload();
    } catch (err) {
      console.error('[prospect] checkoff failed:', err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#0f172a' }}>{SECTION_LABELS.map}</h1>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>
          · {data.actions.length} item{data.actions.length === 1 ? '' : 's'}
        </span>
      </div>

      {data.actions.length === 0 ? (
        <Card><p style={{ margin: 0, color: '#94a3b8' }}>No action items yet.</p></Card>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.actions.map((a) => (
            <ProspectActionRow
              key={a.id}
              action={a}
              accent={accent}
              busy={busyId === a.id}
              onToggle={(checked) => toggle(a, checked)}
              slug={slug}
              token={token}
              onReload={onReload}
              isPreview={!!data.viewer?.isPreview}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ProspectActionRow({ action, accent, busy, onToggle, slug, token, onReload, isPreview }) {
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [posting, setPosting] = useState(false);

  const isDone = action.status === 'done';

  const postComment = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await fetch(`${API_BASE}/api/buyer-spaces/public/${encodeURIComponent(slug)}/actions/${action.id}/comments?t=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: comment.trim() }),
      });
      setComment('');
      onReload();
    } catch (err) {
      console.error('[prospect] comment failed:', err.message);
    } finally {
      setPosting(false);
    }
  };

  return (
    <li style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <input
          type="checkbox"
          checked={isDone}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={!action.canCheckoff || busy}
          style={{
            width: 18, height: 18,
            cursor: action.canCheckoff ? 'pointer' : 'not-allowed',
            opacity: action.canCheckoff ? 1 : 0.5,
          }}
          title={action.canCheckoff
            ? (isDone ? 'Mark as open' : 'Mark as done')
            : 'This item belongs to the seller'}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 500,
            color: isDone ? '#94a3b8' : '#0f172a',
            textDecoration: isDone ? 'line-through' : 'none',
          }}>
            {action.title}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: 12, color: '#64748b' }}>
            <span>Owner: {action.ownerSide}</span>
            {action.dueDate && <span>Due {new Date(action.dueDate).toLocaleDateString()}</span>}
            {isDone && action.completedBySide && (
              <span style={{ color: accent }}>Done by {action.completedBySide}</span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: 'none', border: '1px solid #e2e8f0', borderRadius: 8,
            padding: '6px 12px', cursor: 'pointer', fontSize: 13, color: '#475569',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          {action.comments?.length || 0}
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
          {(action.comments || []).length === 0 ? (
            <p style={{ color: '#94a3b8', fontSize: 13, margin: '4px 0 12px' }}>No comments yet.</p>
          ) : (
            <ul style={{ margin: '4px 0 12px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(action.comments || []).map((c) => (
                <li key={c.id} style={{ background: '#f8fafc', padding: '8px 12px', borderRadius: 6 }}>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    <strong style={{ color: '#0f172a' }}>{c.authorDisplayName || c.authorSide}</strong>
                    {' · '}
                    {new Date(c.createdAt).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap' }}>{c.body}</div>
                </li>
              ))}
            </ul>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={isPreview ? 'Comments can’t be sent from a preview' : 'Add a comment…'}
              disabled={isPreview}
              style={{
                flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1',
                borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') postComment(); }}
            />
            <button
              type="button"
              onClick={postComment}
              disabled={isPreview || !comment.trim() || posting}
              style={{
                padding: '8px 16px', borderRadius: 8, border: 'none',
                background: accent, color: '#fff', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                opacity: (!comment.trim() || posting) ? 0.5 : 1,
              }}
            >
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

// ─── Small primitives ──────────────────────────────────────────

// Per-section hero block — eyebrow tag with section icon + label,
// big H1 with the section title, soft tagline underneath, and a
// decorative gradient blob bleeding from the right edge in the
// seller's brand color. Replaces the bare <h1> we had before. The
// blob is pure decoration, hidden from screen readers.
function SectionHero({ section, accent }) {
  const meta = SECTION_META[section] || {};
  const label = SECTION_LABELS[section] || section;
  return (
    <div style={{
      position: 'relative',
      marginBottom: 24,
      paddingBottom: 18,
      borderBottom: '1px solid rgba(226, 232, 240, 0.7)',
      overflow: 'hidden',
    }}>
      {/* Decorative gradient blob in the brand accent */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: -40, right: -60,
        width: 220, height: 220, borderRadius: '50%',
        background: `radial-gradient(circle, ${accent}26 0%, ${accent}00 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Eyebrow row — icon chip + section meta tag */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '5px 11px 5px 8px', borderRadius: 999,
        background: `${accent}12`, color: accent,
        fontSize: 12, fontWeight: 600, letterSpacing: '0.02em',
        marginBottom: 10,
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {meta.icon}
        </span>
        <span style={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11 }}>
          {meta.eyebrow || 'Section'}
        </span>
      </div>

      <h1 style={{
        margin: 0,
        fontSize: 30, lineHeight: 1.2, fontWeight: 700, color: '#0f172a',
        letterSpacing: '-0.01em',
      }}>
        {label}
      </h1>

      {meta.tagline && (
        <p style={{
          margin: '8px 0 0', fontSize: 15, lineHeight: 1.55,
          color: '#64748b', maxWidth: 640,
        }}>
          {meta.tagline}
        </p>
      )}
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: 14,
      padding: 24,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.08)',
    }}>
      {children}
    </div>
  );
}

function CenteredMessage({ message, tone = 'info' }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
      <div style={{
        maxWidth: 480, padding: '20px 24px', borderRadius: 12,
        background: tone === 'error' ? 'rgba(239, 68, 68, 0.08)' : '#fff',
        border: tone === 'error' ? '1px solid rgba(239, 68, 68, 0.3)' : '1px solid #e2e8f0',
        color: tone === 'error' ? '#dc2626' : '#475569',
        fontSize: 15, textAlign: 'center',
      }}>
        {message}
      </div>
    </div>
  );
}

// Deterministic-but-varied avatar color per person, derived from
// their name so re-renders don't shuffle the palette. Same djb2 +
// HSL-mapping pattern AuditLog uses for actor pills — gives the
// page a consistent cast without us hand-picking colors.
function avatarColor(seed) {
  if (!seed) return { bg: 'rgba(148, 163, 184, 0.18)', fg: '#475569' };
  let h = 5381;
  for (let i = 0; i < seed.length; i += 1) {
    h = ((h << 5) + h + seed.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(h) % 360;
  return {
    bg: `hsl(${hue}, 65%, 92%)`,
    fg: `hsl(${hue}, 50%, 38%)`,
  };
}

function avatarInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

function PeopleCard({ title, people }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: 20,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.08)',
    }}>
      <h3 style={{
        margin: '0 0 14px', fontSize: 12, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b',
      }}>
        {title}
      </h3>
      {people.length === 0 ? (
        <p style={{ color: '#94a3b8', fontSize: 13, margin: 0 }}>—</p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
          {people.map((p) => {
            const palette = avatarColor(p.fullName);
            return (
              <li key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div aria-hidden="true" style={{
                  width: 36, height: 36, flex: 'none',
                  borderRadius: '50%',
                  background: palette.bg, color: palette.fg,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, letterSpacing: '0.02em',
                }}>
                  {avatarInitials(p.fullName)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{p.fullName}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {p.title || ''}{p.title && p.role ? ' · ' : ''}{p.role || ''}
                  </div>
                  {p.email && (
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.email}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── Forward modal ────────────────────────────────────────────
// Top-level "Forward this Decision Room" — visible from every
// section, not buried inside the trailer's share viewer. Reuses
// the existing /api/trailers/share/<token>/forward endpoint which
// mints a child distribution. We rewrite the returned URL so the
// recipient lands on the Decision Room (not the trailer share
// page) and craft a Decision-Room-flavored pre-written message
// the champion can paste straight into their inbox.
function ForwardDecisionRoomModal({ slug, token, accent, branding, prospectCompanyName, onClose }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const sellerCompany = branding?.creatorCompany || 'the team';

  const buildDecisionRoomUrl = (childToken) => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/space/${encodeURIComponent(slug)}?t=${encodeURIComponent(childToken)}`;
  };

  const buildMessage = (recipientName, decisionRoomUrl, optionalNote) => {
    const lines = [
      `Hey ${recipientName},`,
      '',
      `Sharing the Decision Room I've been working through with ${sellerCompany} for ${prospectCompanyName}. Demo trailer, the action plan, the docs we discussed — all in one place.`,
      '',
      decisionRoomUrl,
      '',
    ];
    if (optionalNote) {
      lines.push(optionalNote, '');
    }
    lines.push(`Take a look when you get a chance.`);
    return lines.join('\n');
  };

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const trimmedName = firstName.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName) {
      setError("Enter your teammate's first name.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail)) {
      setError("That doesn't look like a valid email address.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/trailers/share/${encodeURIComponent(token)}/forward`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: trimmedName,
          email: trimmedEmail,
          note: note.trim() || undefined,
          // 'room' forwards the entire Decision Room (carries deal context).
          // Backend gates this to admins-only on the People tab.
          forwardKind: 'room',
        }),
      });
      if (res.status === 429) {
        throw new Error("You've shared too many times from this IP. Try again in a few minutes.");
      }
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.error || "Couldn't create the link. Try again.");
      }
      const data = await res.json();
      const decisionRoomUrl = buildDecisionRoomUrl(data.token);
      setResult({
        url: decisionRoomUrl,
        message: buildMessage(data.recipientName || trimmedName, decisionRoomUrl, note.trim()),
        recipientName: data.recipientName || trimmedName,
        // Captured for the mailto: "Open in email" link below.
        recipientEmail: data.recipientEmail || trimmedEmail,
        reused: Boolean(data.reused),
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // best-effort; older browsers / insecure contexts can fall back to selecting
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Forward this Decision Room"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16,
          width: '100%', maxWidth: 540,
          maxHeight: '90vh', overflowY: 'auto',
          boxShadow: '0 20px 60px -10px rgba(15, 23, 42, 0.35)',
        }}
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Forward this Decision Room</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
              Each forward gets its own tracked link, so {sellerCompany} sees who arrives.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#64748b', padding: 8, borderRadius: 8,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {!result ? (
          <form onSubmit={submit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Their first name</span>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. David"
                disabled={submitting}
                autoFocus
                style={{
                  padding: '10px 12px', borderRadius: 8,
                  border: '1px solid #cbd5e1', fontSize: 14,
                  fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Their email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="david@yourcompany.com"
                disabled={submitting}
                style={{
                  padding: '10px 12px', borderRadius: 8,
                  border: '1px solid #cbd5e1', fontSize: 14,
                  fontFamily: 'inherit',
                }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
                Personal note <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span>
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="One line of context for them, e.g. 'wanted you to see the ROI section'"
                rows={3}
                disabled={submitting}
                style={{
                  padding: '10px 12px', borderRadius: 8,
                  border: '1px solid #cbd5e1', fontSize: 14,
                  fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            </label>

            {error && (
              <div style={{
                padding: '10px 12px', borderRadius: 8,
                background: 'rgba(239, 68, 68, 0.08)',
                color: '#dc2626', fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: 4,
                padding: '11px 16px', borderRadius: 10,
                background: accent, color: '#fff', border: 'none',
                fontSize: 14, fontWeight: 600, cursor: submitting ? 'wait' : 'pointer',
                fontFamily: 'inherit',
                boxShadow: `0 4px 12px ${accent}33`,
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Creating link…' : 'Get tracked link'}
            </button>
          </form>
        ) : (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
              Ready to send to {result.recipientName}
              {result.reused && <span style={{ color: '#64748b', fontWeight: 400, fontSize: 13 }}> (link reused — they already had one)</span>}
            </div>
            <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
              Sending from your own inbox lands far more reliably than a relay from us. One click opens your email client with everything ready to go.
            </div>

            {/* Primary CTA: mailto: link. Opens Gmail/Outlook/Apple Mail
                with To, Subject, and Body prefilled — preserves "from my
                real inbox" delivery + threading. Secondary copy-link
                button covers Slack/Teams paste cases. */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <a
                href={`mailto:${encodeURIComponent(result.recipientEmail || '')}`
                  + `?subject=${encodeURIComponent(`Decision Room — ${sellerCompany}`)}`
                  + `&body=${encodeURIComponent(result.message)}`}
                style={{
                  flex: '1 1 auto',
                  minWidth: 200,
                  padding: '12px 18px',
                  borderRadius: 10,
                  background: accent, color: '#fff',
                  fontSize: 14, fontWeight: 600,
                  textAlign: 'center',
                  textDecoration: 'none',
                  fontFamily: 'inherit',
                  boxShadow: `0 4px 12px ${accent}33`,
                }}
              >
                📧  Open in email
              </a>
              <button
                type="button"
                onClick={() => copy(result.url, 'url')}
                style={{
                  padding: '12px 16px', borderRadius: 10,
                  background: copiedKey === 'url' ? '#10b981' : '#fff',
                  border: `1px solid ${copiedKey === 'url' ? '#10b981' : '#cbd5e1'}`,
                  color: copiedKey === 'url' ? '#fff' : '#334155',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.15s ease',
                }}
              >
                {copiedKey === 'url' ? '✓ Link copied' : 'Copy link'}
              </button>
            </div>

            <details>
              <summary style={{
                cursor: 'pointer',
                fontSize: 13,
                color: '#64748b',
                userSelect: 'none',
                padding: '4px 0',
              }}>
                Preview the message
              </summary>
              <div style={{
                marginTop: 10,
                padding: '12px 14px',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: 13,
                color: '#334155',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}>
                {result.message}
              </div>
              <button
                type="button"
                onClick={() => copy(result.message, 'message')}
                style={{
                  marginTop: 8,
                  padding: '7px 12px', borderRadius: 8,
                  background: copiedKey === 'message' ? '#10b981' : '#fff',
                  border: `1px solid ${copiedKey === 'message' ? '#10b981' : '#cbd5e1'}`,
                  color: copiedKey === 'message' ? '#fff' : '#334155',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {copiedKey === 'message' ? '✓ Copied' : 'Copy message text'}
              </button>
            </details>

            <button
              type="button"
              onClick={onClose}
              style={{
                marginTop: 8,
                padding: '10px 16px', borderRadius: 10,
                background: 'transparent', border: '1px solid #cbd5e1',
                color: '#475569', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RoiTile({ label, value, accent, highlight }) {
  return (
    <div style={{
      position: 'relative',
      padding: 18, borderRadius: 12, overflow: 'hidden',
      background: highlight
        ? `linear-gradient(135deg, ${accent}1f 0%, ${accent}0a 100%)`
        : '#f8fafc',
      border: `1px solid ${highlight ? `${accent}55` : '#e2e8f0'}`,
      boxShadow: highlight ? `0 6px 20px -10px ${accent}66` : 'none',
    }}>
      {highlight && (
        <div aria-hidden="true" style={{
          position: 'absolute', top: -30, right: -30,
          width: 110, height: 110, borderRadius: '50%',
          background: `radial-gradient(circle, ${accent}33 0%, transparent 70%)`,
        }} />
      )}
      <div style={{
        position: 'relative',
        fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.07em',
        color: highlight ? accent : '#64748b',
      }}>
        {label}
      </div>
      <div style={{
        position: 'relative',
        fontSize: highlight ? 28 : 24, fontWeight: 800,
        color: highlight ? accent : '#0f172a',
        marginTop: 8, letterSpacing: '-0.01em',
      }}>
        {value}
      </div>
    </div>
  );
}
